import { useEffect } from 'react';
import { useApplicationInsights } from 'live-lesson/shared/util-insights-react';
import { LiveSessionState } from '@pcp/core/interfaces';

export interface UseLiveLessonAppTrackingProps {
    lessonId: string;
    userId: string;
    displayName: string;
    appType: 'Student' | 'Moderator';
    isMobile: boolean;
    pcpCallState?: LiveSessionState | null;
}

/**
 * Hook to track live lesson application initialization and PCP call state changes with Application Insights.
 * 
 * This hook automatically logs app mount events and PCP call state transitions,
 * providing consistent telemetry across student and moderator applications.
 * 
 * @param props Configuration object with app details and current state
 * @example
 * ```tsx
 * useLiveLessonAppTracking({
 *     lessonId: props.lessonId,
 *     userId: props.userId.communicationUserId,
 *     displayName: props.displayName,
 *     appType: 'Student',
 *     isMobile,
 *     pcpCallState
 * });
 * ```
 */
export const useLiveLessonAppTracking = ({
    lessonId,
    userId,
    displayName,
    appType,
    isMobile,
    pcpCallState
}: UseLiveLessonAppTrackingProps): void => {
    const { trackEvent, logMetric, isInitialized } = useApplicationInsights();

    // Track app initialization
    useEffect(() => {
        if (isInitialized) {
            trackEvent(`LiveLesson${appType}AppMounted`, {
                lessonId,
                userId,
                displayName,
                isMobile,
                deviceType: isMobile ? 'mobile' : 'desktop',
                timestamp: new Date().toISOString()
            });

            // Log device/browser capabilities
            logMetric('AppInitializationTime', Date.now());
        }
    }, [isInitialized, trackEvent, logMetric, lessonId, userId, displayName, appType, isMobile]);

    // Track PCP call state changes
    useEffect(() => {
        if (pcpCallState && isInitialized) {
            trackEvent('PcpCallStateChanged', {
                lessonId,
                callState: pcpCallState,
                appType,
                timestamp: new Date().toISOString()
            });
        }
    }, [pcpCallState, isInitialized, trackEvent, lessonId, appType]);
};